import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Gift, Users, Copy, Check, Share2, Sparkles, ArrowLeft,
  Mail, Trophy, Euro, LayoutDashboard,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

interface ReferralRow {
  referred_email: string;
  status: string;
  created_at: string;
}

export default function ReferralPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [stats, setStats] = useState<{
    total_referrals: number;
    rewarded: number;
    pending: number;
    free_months_earned: number;
    referrals: ReferralRow[];
  }>({ total_referrals: 0, rewarded: 0, pending: 0, free_months_earned: 0, referrals: [] });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [codeRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/referrals/code`, { credentials: "include" }),
          fetch(`${API_URL}/api/referrals/stats`, { credentials: "include" }),
        ]);
        if (codeRes.ok) {
          const d = await codeRes.json();
          setCode(d.code);
          setShareUrl(`${window.location.origin}${d.share_url}`);
        }
        if (statsRes.ok) setStats(await statsRes.json());
      } catch {
        toast.error("Erreur de chargement du parrainage");
      }
    })();
  }, [user]);

  const copyCode = async () => {
    await navigator.clipboard.writeText(shareUrl || code);
    setCopied(true);
    toast.success("Lien de parrainage copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent("Rejoins-moi sur Grafton IA — 1 mois gratuit");
    const body = encodeURIComponent(
      `Bonjour,\n\nJe te recommande Grafton IA, mon copilote IA pour piloter mon activité.\n\nInscris-toi avec mon lien et nous gagnons chacun 1 mois offert :\n\n${shareUrl}\n\nCode : ${code}\n\nÀ très vite !`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Sparkles className="h-8 w-8 animate-pulse text-primary" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="referral-page">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/30 backdrop-blur px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-bold">Parrainage Grafton</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /> Retour</Button>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate("/dashboard")}><LayoutDashboard className="h-4 w-4" /> Dashboard</Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/15 via-card to-card p-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative space-y-4 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wider">Programme Ambassadeur</span>
            </div>
            <h2 className="text-4xl font-bold leading-tight">
              Parrainez un ami,<br />
              <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">gagnez 1 mois gratuit chacun</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-lg leading-relaxed">
              Partagez votre code avec un dirigeant. À chaque inscription validée, vous et votre filleul bénéficiez tous les deux d'un mois gratuit sur votre abonnement Grafton.
            </p>
          </div>
        </div>

        {/* Share block */}
        <Card className="border-border/50">
          <CardContent className="p-8 space-y-6">
            <div className="flex items-center gap-2">
              <Share2 className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold uppercase tracking-wider">Votre lien & code</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center">
              <Input
                value={shareUrl || "Chargement..."}
                readOnly
                className="font-mono text-sm h-12 bg-background/60 border-border/50"
                data-testid="referral-share-url"
              />
              <Button size="lg" className="h-12 gap-2 shrink-0" onClick={copyCode} data-testid="referral-copy-btn">
                {copied ? <><Check className="h-4 w-4" /> Copié</> : <><Copy className="h-4 w-4" /> Copier le lien</>}
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-border/30">
              <div className="flex items-center gap-3">
                <div className="text-xs text-muted-foreground">Votre code</div>
                <div className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 font-mono text-xl font-bold text-primary tracking-wider" data-testid="referral-code">
                  {code || "————"}
                </div>
              </div>
              <Button variant="outline" size="sm" className="gap-2 ml-auto" onClick={shareByEmail} data-testid="referral-email-btn">
                <Mail className="h-4 w-4" /> Envoyer par email
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider"><Users className="h-3.5 w-3.5" /> Filleuls</div>
              <p className="text-3xl font-bold" data-testid="stats-total">{stats.total_referrals}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider"><Trophy className="h-3.5 w-3.5" /> Confirmés</div>
              <p className="text-3xl font-bold text-emerald-400" data-testid="stats-rewarded">{stats.rewarded}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider"><Euro className="h-3.5 w-3.5" /> Mois gagnés</div>
              <p className="text-3xl font-bold text-primary" data-testid="stats-months">{stats.free_months_earned}</p>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="p-6 space-y-1">
              <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider"><Sparkles className="h-3.5 w-3.5" /> En attente</div>
              <p className="text-3xl font-bold text-amber-400" data-testid="stats-pending">{stats.pending}</p>
            </CardContent>
          </Card>
        </div>

        {/* Referrals list */}
        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="px-6 py-4 border-b border-border/30 flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold uppercase tracking-wider">Mes filleuls</h3>
            </div>
            {stats.referrals.length === 0 ? (
              <div className="p-10 text-center">
                <Gift className="h-8 w-8 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm text-muted-foreground">Aucun filleul pour le moment. Partagez votre code pour commencer !</p>
              </div>
            ) : (
              <div className="divide-y divide-border/30">
                <div className="grid grid-cols-3 gap-4 px-6 py-2.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider bg-muted/20">
                  <span>Email</span><span>Date d'inscription</span><span>Statut</span>
                </div>
                {stats.referrals.map((r, i) => (
                  <div key={i} className="grid grid-cols-3 gap-4 px-6 py-3 hover:bg-muted/10 items-center text-sm">
                    <span className="truncate">{r.referred_email}</span>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                    <span>
                      {r.status === "rewarded" ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-[10px] font-semibold">
                          <Check className="h-3 w-3" /> Récompensé
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-[10px] font-semibold">
                          En attente
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { n: 1, title: "Partagez votre code", desc: "Envoyez votre lien unique à un dirigeant qui pourrait bénéficier de Grafton." },
            { n: 2, title: "Votre filleul s'inscrit", desc: "Il crée son compte via votre lien. Le code est automatiquement appliqué." },
            { n: 3, title: "Vous gagnez un mois", desc: "Un mois gratuit est crédité pour vous et votre filleul dès la validation." },
          ].map((s) => (
            <Card key={s.n} className="border-border/50">
              <CardContent className="p-6 space-y-3">
                <div className="h-8 w-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary font-bold">{s.n}</div>
                <h4 className="text-sm font-semibold">{s.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
